import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { subDays } from "date-fns";
import { ToastContainer, toast } from "react-toastify";

import NewsAggSearchBar from "../../Views/NewsAggregator/SearchBar/NewsAggSearchBar";
import AggregatorNewsTab from "../../Views/NewsAggregator/AggregatorNewsTab/AggregatorNewsTab";
import NoResultsBox from "../../Views/Common/NoResultsBox/NoResultsBox";
import NewSourceDistChart from "../../Views/Graphs/NewsAggregator/NewSourceDistChart";
import AggTreeMapChart from "../../Views/Graphs/NewsAggregator/AggTreeMapChart";
import CandleStick from "../../Views/Graphs/NewsAggregator/CandleStick";
import HeatMapChart from "../../Views/Graphs/NewsAggregator/HeatMapChart";
import AggStackedBarLine from "../../Views/Graphs/NewsAggregator/AggStackedBarLine/AggStackedBarLine";

import Navbar from "../LandingV2/Navbar";

import { getSortOrFilteredArr, getUniqueOptions } from "../../utils/Common";
import {
  filterAndCombineNewsSourceDist,
  filterCandleStickData,
  filterFrequentEntities,
  filterHeatMapData,
  filterNewsTabData,
  filterStackBarLine,
  getAllNewsAggOptionsData,
  getDatesBetweenDates,
  getDifferenceInDays,
} from "../../utils/NewAgrregator";
import useWindowDimemsions from "../../hooks/useWindowDimesnions";
import "react-toastify/dist/ReactToastify.css";
import "./NewsAggregator.scss";
import { Spin } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

//remove newsletter from aggregator path

const aggregatorHighlightEnum = {
  AggStackedBarLine: 0,
  NewSourceDistChart: 1,
  AggTreeMapChart: 2,
  CandleStick: 3,
  HeatMapChart: 4,
  AggregatorNewsTab: 5,
};

const aggregatorInfoBoxText = {
  [aggregatorHighlightEnum.AggStackedBarLine]: "Shows the distribution of news volume and sentiment (positive/negative/neutral) across days.",
  [aggregatorHighlightEnum.NewSourceDistChart]: "Displays a breakdown of news articles by their publishing source within the selected date range.",
  [aggregatorHighlightEnum.AggTreeMapChart]: "Visualizes the most frequently mentioned organizations or entities in the news.",
  [aggregatorHighlightEnum.CandleStick]: "Shows the stock's open, high, low, and closing prices over the selected period.",
  [aggregatorHighlightEnum.HeatMapChart]: "Highlights the density of specific event types associated with the company over time.",
  [aggregatorHighlightEnum.AggregatorNewsTab]: "Presents a filterable and sortable list of raw news articles, ordered chronologically or by relevance.",
};

const toastSettings = {
  position: "top-center",
  autoClose: 200,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: false,
  draggable: false,
  progress: undefined,
  theme: "light",
};

/**
 * NewsAggregator Component.
 *
 * This component provides a user interface for aggregating and analyzing news data
 * related to various companies within a specified date range. Users can select a company,
 * choose a date range, and view various charts and graphs representing news sentiment,
 * source distribution, and more.
 *
 * This function is called in \src\Router\Router.tsx
 *
 * @component
 */
const NewsAggregator = () => {
  const [startDate, setStartDate] = useState(subDays(new Date(), 10));
  
  // Page Tour State
  const [showInfoBox, setShowInfoBox] = useState(false);
  const [highlightBox, sethighlightBox] = useState(aggregatorHighlightEnum.AggStackedBarLine);
  const infoBoxRefList = useRef([]);

  const setInfoBoxRef = (index) => (ref) => {
    infoBoxRefList.current[index] = ref;
  };

  const handleInfoBox = (boxType) => {
    if (showInfoBox && highlightBox === boxType) {
      setShowInfoBox(false);
    } else {
      sethighlightBox(boxType);
      setShowInfoBox(true);
    }
  };
  const [endDate, setEndDate] = useState(new Date());

  // Searchbar state
  const [tickerOptions, setTickerOptions] = useState([]);

  //  //new logic
  // const [selectedStartDate, setSelectedstartDate] = useState(null);
  // const [selectedEndDate, setSelectedEndDate] = useState(null);

  //  //new logic
  // const [freqEntitiesDate, setFreqEntitiesDate] = useState(null);   //sub date range for most freq Entities
  // const [newsSourceDistDate, setNewsSourceDistDate] = useState(null);   //sub date range for News Source Dist

  const [selectedCompany, setSelectedCompany] = useState(null);

  const [errors, setErrors] = useState({
    startDates: false,
    endDates: false,
    companyName: false,
    startDatesErrMsg: "From Date is required",
    endDatesErrMsg: "Till Date is required",
  });

  const [isLoaderVisible, setIsLoaderVisible] = useState(false);

  const [allData, setAllData] = useState({}); //remove later

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [sortValue, setSortValue] = useState("");
  const [optionsData, setOptionsData] = useState([]);

  const [newsTabData, setNewsTabData] = useState([]);
  const [newsTabOriginalData, setNewsTabOriginalData] = useState([]);

  const [candleStickData, setCandleStickData] = useState([]);
  const [newsSentimentVsVolume, setNewsSentimentVsVolume] = useState([]);
  const [heatMapData, setHeatMapData] = useState([]);
  const [newsSourceDist, setNewsSourceDist] = useState({}); //check if array or object
  const [mostFrequentEntities, setMostFrequentEntities] = useState([]);

  //new logic
  // const [allDataFrequentEntities, setAllDataFrequentEntities] = useState({});
  // const [allDataNewsSourceDist, setAllDataNewsSourceDist] = useState({});

  const getTickersData = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_NEWS_AGGREGATOR}/Dynamo_DB_Data.json`
      );
      const options = Array.isArray(data)
        ? data.map((ticker) => ({
            value: ticker.Stock_Ticker_Symbol,
            label: ticker.Ticker_Name,
          }))
        : [];
      setTickerOptions(options);
    } catch (err) {
      console.error("Failed to fetch ticker data", err);
      setTickerOptions([]);
    }
  };

  useEffect(() => {
    getTickersData();
  }, []);

  useEffect(() => {
    populateOptions(newsTabOriginalData); //reset
    setSortValue(""); //reset
    setSearchValue(null); //reset
  }, [newsTabOriginalData]);

  useEffect(() => {
    const result = getSortOrFilteredArr(
      newsTabOriginalData,
      searchValue,
      sortValue,
      "news_source",
      "news_date",
      "News_Sentiment"
    ); //"News_Sentiment" currently not present in data
    setNewsTabData(result);
  }, [searchValue, sortValue, newsTabOriginalData]);

  useEffect(() => {
    let errorss = {};

    if (startDate === null) {
      errorss = {
        ...errorss,
        startDates: true,
        startDatesErrMsg: "From Date is required",
      };
    } else if (endDate === null) {
      errorss = {
        ...errorss,
        endDates: true,
        endDatesErrMsg: "Till Date is required",
      };
    } else if (
      startDate !== null &&
      endDate !== null &&
      getDifferenceInDays(startDate, endDate) >= 0
    ) {
      // !==null not needed(already checked) but added cause getDifferenceInDays needs them
      const msg = "From Date must be before Till Date";
      errorss = {
        ...errorss,
        startDates: true,
        endDates: true,
        startDatesErrMsg: msg,
        endDatesErrMsg: msg,
      };
    } else if (
      startDate !== null &&
      endDate !== null &&
      getDifferenceInDays(startDate, endDate) <= -20
    ) {
      // max limit for graph date range is 20 days
      const msg = "max limit is 20 days";
      errorss = {
        ...errorss,
        startDates: true,
        endDates: true,
        startDatesErrMsg: msg,
        endDatesErrMsg: msg,
      };
    } else {
      errorss = {
        ...errorss,
        startDates: false,
        startDatesErrMsg: "From Date is required",
        endDates: false,
        endDatesErrMsg: "Till Date is required",
      };
    }

    setErrors((errors) => ({ ...errors, ...errorss }));
  }, [startDate, endDate]);

  const populateOptions = (data) => {
    const result = getUniqueOptions(data, "news_source");
    setOptionsData(result);
  };

  const handleSortByScore = (event) => {
    setSortValue(event);
  };

  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
  };

  const handleSearchChange = (value) => {
    if (value) {
      setSearchValue(value);
    } else {
      //!value always null by default for antd
      setSearchValue(null);
    }

    setOpen(false);
  };

  const handleShow = () => {
    setOpen(true);
  };

  const handleSearchSumbiit = async (event) => {
    event.preventDefault();

    if (
      errors.startDates === true ||
      errors.endDates === true ||
      errors.companyName === true
    ) {
      return;
    } else if (
      startDate === null ||
      endDate === null ||
      selectedCompany === null
    ) {
      //stop execution
      const errorss = {
        ...errors, // prev errors
        startDates: startDate === null ? true : false,
        endDates: endDate === null ? true : false,
        companyName: selectedCompany === null ? true : false,
      };
      setErrors(errorss);
      return;
    }

    //console.log('code below')

    //reset state
    handleResetState();

    try {
      //get dates
      const dateRange = getDatesBetweenDates(startDate, endDate);
      //console.log('dateRange', dateRange)

      //new logic
      // setSelectedstartDate(() => startDate);
      // setSelectedEndDate(() => endDate);

      setIsLoaderVisible(true);

      //https://algoanalytics-fabric-website.s3.ap-south-1.amazonaws.com/Json_copy/Json/TATAMOTORS.NS/news_aggregator.json
      const { data: allPossibleData } = await axios.get(
        `${process.env.REACT_APP_NEWS_AGGREGATOR}/${selectedCompany}/news_aggregator.json`
      );

      setAllData(() => allPossibleData); //was prev used to pass data to graph, now just used render diff divs/layouts

      const aggNewTabData = allPossibleData.news_articles || {};
      const filteredNewTabData = filterNewsTabData(aggNewTabData, dateRange); //filterNewsTabData  returns array
      setNewsTabData(() => filteredNewTabData);
      setNewsTabOriginalData(() => [...filteredNewTabData]);

      const candleData = allPossibleData.Stock_data || {};
      setCandleStickData(() => filterCandleStickData(candleData, dateRange)); //filterCandleStickData returns array

      const barGraphData = allPossibleData.sentiment_stacked_barchart || {};
      const lineGraphData = allPossibleData.news_distrib_across_days || {};
      const stackedBarLine = filterStackBarLine(
        barGraphData,
        lineGraphData,
        dateRange
      ); //filterStackBarLine returns array
      setNewsSentimentVsVolume(() => stackedBarLine);

      const heatMap = allPossibleData.event_label_heatmap || {};
      setHeatMapData(() => filterHeatMapData(heatMap, dateRange)); //filterHeatMapData returns array

      const newsSourceDistribution = allPossibleData.news_source_distrib || {};
      const finalData = filterAndCombineNewsSourceDist(
        newsSourceDistribution,
        dateRange
      );
      setNewsSourceDist(() => finalData); //filterAndCombineNewsSourceDist returns object
      //new logic
      // handleNewsSourceDistDate(startDate);
      // setAllDataNewsSourceDist(() => newsSourceDistribution); //all data

      const mostFrequentEntities = allPossibleData.ner_org_freq || {};
      const entityData = filterFrequentEntities(
        mostFrequentEntities,
        dateRange
      );
      setMostFrequentEntities(() => entityData); //filterAndCombineNewsSourceDist returns array
      //console.log('setMostFrequentEntities', entityData);
      //new logic
      // handleFrequentEntitiesDate(startDate); //default date
      // setAllDataFrequentEntities(() => mostFrequentEntities); //all data

      toast.success("Success", toastSettings);
    } catch (err) {
      // if (err.response && err.response.status >= 400) {
      toast.error("No news articles found", toastSettings);
      //};
      handleResetState();
    } finally {
      setIsLoaderVisible(false);
    }
  };

  const handleCompanyChange = (value) => {
    if (value) {
      setSelectedCompany(value);
      setErrors({ ...errors, companyName: false });
    } else {
      setSelectedCompany(null);
      setErrors({ ...errors, companyName: true });
    }
  };

  const handleStartDate = (date) => {
    setStartDate(date);
  };

  const handleEndDate = (date) => {
    setEndDate(date);
  };

  const handleResetState = () => {
    //console.log('handleResetState');

    //reset
    setAllData(() => {});
    setNewsTabData(() => []);
    setNewsTabOriginalData(() => []);

    setCandleStickData(() => []);
    setNewsSentimentVsVolume(() => []);
    setHeatMapData(() => []);
    setNewsSourceDist(() => {});
    setMostFrequentEntities(() => []);

    // //new logic
    // handleNewsSourceDistDate(null); //default date
    // setAllDataNewsSourceDist(() => {}); //all data

    // //new logic
    // handleFrequentEntitiesDate(null); //default date
    // setAllDataFrequentEntities(() => {}); //all data

    // //new logic
    // setSelectedstartDate(() => null);
    // setSelectedEndDate(() => null);
  };

  // const handleFrequentEntitiesDate = (date) => {
  //     setFreqEntitiesDate(date);
  // };

  // const handleNewsSourceDistDate = (date) => {
  //     setNewsSourceDistDate(date);
  // };

  const { width } = useWindowDimemsions();

  // const filteredFrequentEntities = useMemo(
  //     () => filteredFreqEntities(allDataFrequentEntities, freqEntitiesDate),
  //     [allDataFrequentEntities, freqEntitiesDate]
  // );

  // const filteredNewsSourceDistrib = useMemo(
  //     () => filterNewsSourceDist(allDataNewsSourceDist, newsSourceDistDate),
  //     [allDataNewsSourceDist, newsSourceDistDate]
  // );

  useEffect(() => {
    if (showInfoBox && infoBoxRefList.current) {
      const cardRef = infoBoxRefList.current[highlightBox];
      if (!cardRef) {
        console.log("Could not get bounding ref");
        return;
      }

      const cardRect = cardRef.getBoundingClientRect();
      
      // Calculate absolute scroll position with a 180px offset to accommodate sticky headers
      const absoluteY = cardRect.top + window.scrollY - 180;
      window.scrollTo({ top: absoluteY, behavior: "smooth" });
      const infoBox = document.getElementById("infoBox");
      if (infoBox) {
        let infoBoxWidth = infoBox.offsetWidth;
        const viewportWidth = window.innerWidth;
        let leftPosition = cardRect.right + window.scrollX + 20;
        let topPosition = cardRect.top + window.scrollY;

        // Give it sensible default dimensions when rendering on desktop
        infoBox.style.width = "250px";
        infoBox.style.fontSize = "14px";
        infoBox.style.height = "auto";
        // Ensure no leftover mobile styling corrupts the desktop layout
        infoBoxWidth = 250;

        // Dynamically figure out if the right side is too close to the edge of the screen
        // If it crosses the middle of the viewport (or offscreen), place to the left instead
        if (cardRect.right + infoBoxWidth + 50 > viewportWidth) {
          leftPosition = cardRect.left + window.scrollX - infoBoxWidth - 30;
        }

        // Prevent the infobox from rendering off completely on the left side of the screen!
        leftPosition = Math.max(15, leftPosition);

        if (viewportWidth < 768) {
          // Mobile stack view - place directly under the card
          leftPosition = Math.max(20, (viewportWidth - infoBoxWidth) / 2); // Center horizontally roughly
          infoBox.style.width = "180px";
          infoBox.style.fontSize = "12px";
          topPosition = cardRect.bottom + window.scrollY + 5;
        }
        infoBox.style.top = `${topPosition}px`;
        infoBox.style.left = `${leftPosition}px`;
        infoBox.style.zIndex = "1040";
      }
    }
  }, [showInfoBox, highlightBox]);

  useEffect(() => {
    if (showInfoBox) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = "hidden";
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      return () => {
        document.body.style.overflow = originalStyle;
        document.body.style.paddingRight = "";
      };
    }
  }, [showInfoBox]);

  return (
    <>
      <style>{`
        .tour-wrapper > div[class*="col-"] {
           max-width: 100% !important;
           flex: 0 0 100% !important;
           width: 100% !important;
        }
      `}</style>
      <Navbar />
      {showInfoBox && (
        <>
          <div
            id="infoBox"
            className="info-box"
            style={{
              position: "absolute",
              transition: "top 0.4s ease, left 0.4s ease",
              zIndex: 1040,
              backgroundColor: "#fff",
              boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
              borderRadius: "12px",
              borderTop: "6px solid #4285F4",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            }}
          >
            <div style={{ padding: "24px 24px 20px 24px" }}>
              <div className="info-box-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <div className="ib-title" style={{ fontSize: "14px", fontWeight: "700", color: "#4285F4", letterSpacing: "1px" }}>PAGE TOUR</div>
                <span
                  className="close-x"
                  onClick={() => setShowInfoBox(false)}
                  style={{ cursor: "pointer", color: "#64748b" }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </span>
              </div>
              <div className="info-box-content" style={{ fontSize: "15.5px", color: "#475569", lineHeight: "1.6", margin: 0 }}>
                {aggregatorInfoBoxText[highlightBox]}
              </div>
            </div>
            <div className="info-box-bottom" style={{ display: "flex", justifyContent: "center", backgroundColor: "#f8fafc", padding: "16px 24px", borderTop: "1px solid #f1f5f9" }}>
              <div className="pagination-container" style={{ display: "flex", alignItems: "center", gap: "24px" }}>
                <span
                  className="nav-arrow"
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", borderRadius: "8px", backgroundColor: "#e8f0fe", color: "#4285F4", transition: "background-color 0.2s ease" }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#d2e3fc"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#e8f0fe"}
                  onClick={() =>
                    sethighlightBox((prev) => {
                      const max = Object.keys(aggregatorHighlightEnum).length - 1;
                      return prev === 0 ? max : prev - 1;
                    })
                  }
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </span>
                <span className="page-info" style={{ fontSize: "14px", fontWeight: "700", color: "#1e293b", minWidth: "46px", textAlign: "center" }}>
                  {highlightBox + 1} <span style={{ color: "#94a3b8", margin: "0 6px", fontWeight: "500" }}>/</span> {Object.keys(aggregatorHighlightEnum).length}
                </span>
                <span
                  className="nav-arrow"
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", borderRadius: "8px", backgroundColor: "#e8f0fe", color: "#4285F4", transition: "background-color 0.2s ease" }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#d2e3fc"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#e8f0fe"}
                  onClick={() =>
                    sethighlightBox((prev) => {
                      const max = Object.keys(aggregatorHighlightEnum).length - 1;
                      return prev === max ? 0 : prev + 1;
                    })
                  }
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </span>
              </div>
            </div>
          </div>
          <div className="modal-backdrop" style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.4)", zIndex: 1030 }}></div>
        </>
      )}
      <NewsAggSearchBar
        onSearchSumbiit={handleSearchSumbiit}
        selectedCompany={selectedCompany}
        onCompanyChange={handleCompanyChange}
        options={tickerOptions}
        errors={errors}
        startDate={startDate}
        onChangeStartDate={handleStartDate}
        endDate={endDate}
        onChangeEndDate={handleEndDate}
      />

      <Spin spinning={isLoaderVisible}>
        <section>
          <div className="container-fluid">
            <div>
              {Object.keys(allData || {})?.length > 0 ? (
                <>
                  {/* <div className="date-range-cards-flex-box mx-auto" >

                                        <SubDateRange
                                            title={"Most Frequent Entities"}
                                            key={"freqEntitiesDate"}
                                            value={freqEntitiesDate}
                                            onChange={handleFrequentEntitiesDate}
                                            minDate={new Date(selectedStartDate)}
                                            maxDate={new Date(selectedEndDate)}
                                        />


                                        <SubDateRange
                                            title={"News Source Distribution"}
                                            key={"newsSourceDistDate"}
                                            value={newsSourceDistDate}
                                            onChange={handleNewsSourceDistDate}
                                            minDate={new Date(selectedStartDate)}
                                            maxDate={new Date(selectedEndDate)}
                                        />

                                </div>

                                <div className="row mx-auto" >

                                        <AggTreeMapChart
                                            key={"three"}
                                            data={filteredFrequentEntities}
                                        />

                                        <NewSourceDistChart
                                            key={"four"}
                                            data={filteredNewsSourceDistrib}
                                        />

                                </div> */}

                  <div className="d-flex justify-content-end align-items-center mb-3" style={{ paddingRight: "15px" }}>
                     <div 
                        className="premium-tour-btn" 
                        onClick={() => handleInfoBox(0)}
                     >
                        <InfoCircleOutlined style={{ fontSize: "16px" }} />
                        <span>What's this?</span>
                     </div>
                  </div>

                  <div className="row mx-auto">
                    <div className="col-xl-8">
                      <div className="row mb-2">
                        <div
                          className="col-md-6 tour-wrapper"
                          ref={setInfoBoxRef(aggregatorHighlightEnum.AggStackedBarLine)}
                          style={showInfoBox && highlightBox === aggregatorHighlightEnum.AggStackedBarLine ? { position: 'relative', zIndex: 1050 } : {}}
                        >
                          <AggStackedBarLine data={newsSentimentVsVolume} />
                        </div>

                        <div
                          className="col-md-6 tour-wrapper"
                          ref={setInfoBoxRef(aggregatorHighlightEnum.NewSourceDistChart)}
                          style={showInfoBox && highlightBox === aggregatorHighlightEnum.NewSourceDistChart ? { position: 'relative', zIndex: 1050 } : {}}
                        >
                          <NewSourceDistChart
                            //  key={"one"}
                            data={newsSourceDist}
                          />
                        </div>
                      </div>

                      <div className="row  mb-2">
                        <div
                          className="col-md-6 tour-wrapper"
                          ref={setInfoBoxRef(aggregatorHighlightEnum.AggTreeMapChart)}
                          style={showInfoBox && highlightBox === aggregatorHighlightEnum.AggTreeMapChart ? { position: 'relative', zIndex: 1050 } : {}}
                        >
                          <AggTreeMapChart
                            // key={"two"}
                            data={mostFrequentEntities}
                          />
                        </div>

                        <div
                          className="col-md-6 tour-wrapper"
                          ref={setInfoBoxRef(aggregatorHighlightEnum.CandleStick)}
                          style={showInfoBox && highlightBox === aggregatorHighlightEnum.CandleStick ? { position: 'relative', zIndex: 1050 } : {}}
                        >
                          <CandleStick data={candleStickData} />
                        </div>
                      </div>

                      <div className="row  mb-2">
                        <div
                          className="col-md-12 tour-wrapper"
                          ref={setInfoBoxRef(aggregatorHighlightEnum.HeatMapChart)}
                          style={showInfoBox && highlightBox === aggregatorHighlightEnum.HeatMapChart ? { position: 'relative', zIndex: 1050 } : {}}
                        >
                          <HeatMapChart data={heatMapData} screenWidth={width} />
                        </div>
                      </div>
                    </div>

                    <div 
                      className="col-xl-4 tour-wrapper"
                      ref={setInfoBoxRef(aggregatorHighlightEnum.AggregatorNewsTab)}
                      style={showInfoBox && highlightBox === aggregatorHighlightEnum.AggregatorNewsTab ? { position: 'relative', zIndex: 1050, height: '100%' } : { height: '100%' }}
                    >
                      <AggregatorNewsTab
                        data={newsTabData}
                        open={open}
                        onOpenChange={handleOpenChange}
                        onShow={handleShow}
                        optionsData={optionsData}
                        onSearchChange={handleSearchChange}
                        searchValue={searchValue}
                        onsortByScore={handleSortByScore}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div
                  className=" mb-4 row "
                  style={{
                    background: "#fff",
                    borderRadius: "5px",
                    padding: "20px",
                    marginLeft: "auto",
                    marginRight: "auto",
                    width: "100%",
                  }}
                >
                  <div className="col">
                    <div
                      style={{
                        minHeight: "65svh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <NoResultsBox
                        displayText={
                          isLoaderVisible ? `searching...` : `Select Company`
                        }
                        showEmptyImage={true}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            :
          </div>
        </section>
      </Spin>

      <ToastContainer />
      {/* {isLoaderVisible && (
                <div className="loader">
                    {" "}
                    <CircularProgress />
                </div>
            )} */}
    </>
  );
};

export default NewsAggregator;
